/**
 * 묶음 위임 형제 바 — 외부(토큰) 작성 화면 상단의 **카운터 + 건 간 이동**(S11).
 *
 * 결정: `docs/decisions/2026-09-06-tbm-post-submit-distribution-and-delegation.md` §6.
 *
 * 인증 화면의 `SiblingReportChips`를 쓰지 못하는 이유는 도메인이 아니라 **재료**다: 그쪽은
 * `useSiblingReports`(인증 목록 API)로 형제를 직접 조회하고 `Report` 전체를 먹는다. 무인증
 * 경로엔 그 API가 없고, 대신 서버가 draft 응답에 `splitSiblings`(형제 5필드 투영)를 동봉한다.
 * 그래서 여기는 **주어진 목록을 그리기만** 한다 — 조회도 라우팅도 하지 않는다(이동은 호스트가
 * `?reportId=`로 같은 토큰을 재조회하는 것이고, 외부엔 문서 상세 URL 자체가 없다).
 *
 * 노출하는 것은 서버가 준 다섯 필드뿐이다 — 위임자·조직 내부 정보는 응답에 없고 여기서
 * 만들어 내지도 않는다(외부 화면 규율).
 */
import { Card } from '@safetyhub/ui';
import type { ComposedExternalSplitSibling } from '../../../lib/composed-external';
import { disambiguateSiblingLabels } from '../../../lib/siblingLabels';

/**
 * 아직 **작성이 필요한** 형제인가 — 카운터 분자(k)의 여집합. **표시 축 전용이다.**
 *
 * 서버가 토큰을 살려 두는 잔여 술어(`completeDraftByToken`의 `remainingCreatingSiblings`)가
 * `CREATING`이라 그것이 수명 축의 정본이다. 여기에 `REJECTED`를 더한 이유는 화면 정합이다:
 * 반려된 건은 페이지가 "내용을 수정한 후 다시 완료해주세요" 배너를 띄우면서 카운터에서는
 * 완료로 세면 두 표시가 서로 반박한다.
 *
 * ⚠️ **이 술어를 서버 잔여 술어와 통일하지 말 것 — 축이 둘이고 REJECTED 경계에서 갈린다.**
 *  - *표시 축*(여기): "담당자가 아직 손볼 것이 남았나" → `CREATING | REJECTED`.
 *  - *토큰 수명 축*(서버): "이 토큰을 살려 둘 이유가 있나" → `CREATING`만. 반려 건은 이 토큰이
 *    아니라 **반려 릴레이**(위임자 경유 재전송, `2026-08-26-delegation-rejection-relay-*`)가
 *    토큰을 다시 여는 별개 경로라, 서버가 여기서 REJECTED를 세면 아무도 안 쓰는 토큰이
 *    만료까지 살아 있게 된다.
 *  둘을 한쪽으로 맞추면 반드시 한쪽이 틀린다 — 카운터가 반려 건을 완료로 세거나(표시 모순),
 *  토큰이 필요 없이 살아 있거나. 그래서 **이동 제안(`nextWritableSibling`)은 수명 축을 따른다**:
 *  같은 화면에서 카운터는 "남았다"고 말하는데 갈 수 있는 문은 없는 상태가 정상이다.
 *
 * ⚠️ 완료를 `DRAFT`로 좁혀 세면 안 된다. `policy.postSubmit` **비선언** 템플릿은 외부 완료가
 * 결재 자동 시작을 태워 문서가 곧바로 `PENDING_APPROVAL`이 되므로, DRAFT만 세는 카운터는
 * 완료할수록 되레 줄어든다. 선언 템플릿(§6의 본류)에서는 완료건이 DRAFT에 머물러 두 정의가
 * 같은 값이 된다. `CANCELLED`는 서버 형제 술어가 이미 제외해 목록에 오지 않는다.
 */
export function isSiblingPending(sibling: ComposedExternalSplitSibling): boolean {
  return sibling.status === 'CREATING' || sibling.status === 'REJECTED';
}

/**
 * 이 문서 다음으로 **이어 작성할 수 있는** 형제 — 목록 순서(서버 생성순) 기준 첫 `CREATING`.
 *
 * ⚠️ 여기만 `isSiblingPending`보다 좁은 이유: 서버는 **CREATING 형제가 0이 되는 순간 토큰을
 * 비활성화한다**(`completeDraftByToken`). 그래서 마지막 CREATING을 끝낸 뒤 남은 REJECTED 건을
 * "이어 작성"으로 안내하면 링크가 만료 화면으로 떨어진다 — 해결 경로 없는 막다른 길이다.
 * 반려 건의 출구는 반려 릴레이(위임자 경유 재전송)이지 이 토큰이 아니다. 카운터·칩이
 * REJECTED를 미완으로 세는 것(`isSiblingPending`)과는 **별개 축**이다: 그쪽은 "무엇이 남았나"를,
 * 이쪽은 "지금 이 토큰으로 갈 수 있나"를 답한다(두 축의 경계 설명은 그 술어의 doc이 정본).
 */
export function nextWritableSibling(
  siblings: ComposedExternalSplitSibling[],
  excludeReportId?: string,
): ComposedExternalSplitSibling | null {
  return (
    siblings.find((s) => s.reportId !== excludeReportId && s.status === 'CREATING') ?? null
  );
}

/**
 * 칩 **기본** 라벨 — 서버가 박제한 그룹 표시명을 **그대로 인용**한다. 무엇으로 묶였는지
 * (장소·작업 등)를 이 컴포넌트가 알 이유가 없고, 알아서도 안 된다(분류 어휘를 화면이 지어내면
 * provider가 바뀔 때 거짓말이 된다). 라벨이 비면 키로, 그것도 없으면 순번으로 degrade.
 *
 * ⚠️ 이것으로 끝이 아니다 — 중복 해소(`disambiguateSiblingLabels`)를 **목록 단위로** 한 번 더
 * 거친다. 행 축 전환(2026-09-10) 이후 같은 `splitGroupLabel` 형제가 둘 생길 수 있어서다.
 * 폴백 사다리가 인증 화면(`SiblingReportChips`, 제목으로 degrade)과 다른 것은 **재료가 달라서**
 * 정당하다(여기엔 `title`이 없다) — 공유하는 것은 중복 해소 규칙 하나다.
 */
function siblingLabel(sibling: ComposedExternalSplitSibling, index: number): string {
  return sibling.splitGroupLabel || sibling.splitGroupKey || `${index + 1}번째 문서`;
}

/**
 * 상태 표시 — 담당자가 할 일 기준(작성 여부만 말한다. 결재 진행은 외부 관심사 아님).
 *
 * **3값이다**(2026-09-10). 종전엔 `status`만 보아 「작성 전 / 작성 완료」 둘이었는데, 임시 저장은
 * 승격하지 않으므로(「DRAFT = 작성 완료된 초안」 규약) **반쯤 쓰다 만 문서도 「작성 전」**이었다 —
 * 담당자 본인조차 어디까지 했는지 화면이 안 알려 줬다(사용자 적발). 문서별 저장 표식
 * (`hasDelegateDraft`)이 그 사이를 메운다. 옛 응답(undefined)은 종전 2값으로 degrade한다.
 *
 * ⚠️ 이 어휘는 **코드 소유**다 — 저작 축은 `policy.split`(분할을 쓰는가)까지이고, 진행 어휘를
 * 저작자가 바꾸면 위임자 화면·카운터·서버 판정과 갈린다(`REPORT_STATUS_LABELS`와 같은 계열).
 * admin 미리보기에 이 바가 없는 것은 「작성 페이지 chrome 모사」가 아직 없어서다 — 은닉이 아니라
 * 모사 축의 경계다(미리보기 후보 기준은 live 바인딩이고 이 바는 호스트 chrome이다).
 */
function siblingStateLabel(sibling: ComposedExternalSplitSibling): string {
  if (sibling.status === 'REJECTED') return '수정 필요';
  if (!isSiblingPending(sibling)) return '작성 완료';
  return sibling.hasDelegateDraft ? '작성 중' : '작성 전';
}

export interface SplitSiblingBarProps {
  siblings: ComposedExternalSplitSibling[];
  /** 지금 작성 중인 문서 — 현재 칩 판정 축. */
  currentReportId: string;
  /** 다른 건으로 이동. 현재 문서 칩에는 배선하지 않는다. */
  onSelect: (reportId: string) => void;
  /** 저장·완료 진행 중 — 이동을 막는다(작성분이 저장되기 전에 화면이 갈리지 않게). */
  disabled?: boolean;
}

export function SplitSiblingBar({
  siblings,
  currentReportId,
  onSelect,
  disabled = false,
}: SplitSiblingBarProps) {
  // 묶음이 아니거나(형제 1건) 목록이 비면 그릴 것이 없다 — 노출은 파생이지 플래그가 아니다.
  if (siblings.length < 2) return null;

  const done = siblings.filter((s) => !isSiblingPending(s)).length;
  // 중복 해소는 인증 화면(`SiblingReportChips`)과 **같은 함수**를 쓴다 — 갈리면 위임자 화면과
  // 담당자 화면이 같은 형제를 다르게 부른다. 목록 순서(서버 생성순)가 순번의 축이다.
  const labels = disambiguateSiblingLabels(siblings.map(siblingLabel));

  return (
    <Card
      padding="none"
      elevation="none"
      className="mb-4 px-4 py-3"
      data-testid="split-sibling-bar"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-ink-800">
          작성 완료 ({done}/{siblings.length})
        </span>
        <span className="text-xs text-ink-500">
          {done === siblings.length
            ? '모든 문서를 작성했습니다.'
            : '문서를 눌러 이동할 수 있습니다.'}
        </span>
      </div>
      <ul className="mt-2 flex flex-wrap gap-2">
        {siblings.map((sibling, index) => {
          const isCurrent = sibling.reportId === currentReportId;
          const label = labels[index];
          return (
            <li key={sibling.reportId}>
              <button
                type="button"
                // 현재 문서는 이동 대상이 아니다 — 누를 수 있는 것처럼 보이면 재조회만 돈다.
                disabled={isCurrent || disabled}
                onClick={() => onSelect(sibling.reportId)}
                aria-current={isCurrent ? 'true' : undefined}
                className={`rounded-full border px-3 py-1 text-xs transition ${
                  isCurrent
                    ? 'border-brand-500 bg-brand-50 font-semibold text-brand-700'
                    : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300 disabled:opacity-50'
                }`}
              >
                {label}
                <span className="ml-1 text-ink-400">· {siblingStateLabel(sibling)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export default SplitSiblingBar;
